import { PermissionCode, type FleetOperationalStatus } from '@gb-ticket/shared-types';
import { lazy, Suspense, useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { usePageTitle } from '@/hooks/use-page-title';
import { hasPermission } from '@/platform/auth/permissions';
import { useMe } from '@/platform/auth/use-me';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import {
  formatDeDateTime,
  FLEET_TELEMATICS_ISSUE_LABELS,
} from '@/products/fleet/features/fleet-labels';
import { fleetTableFrameClassName } from '@/products/fleet/features/fleet-panel';
import { FleetNoSignalBadge } from '@/products/fleet/features/fleet-no-signal-badge';
import type { FleetMapMarker } from '@/products/fleet/features/fleet-position-map';
import { FleetStatusBadge } from '@/products/fleet/features/fleet-status-badge';
import {
  useFleetPolicy,
  useFleetSummary,
  useFleetVehicles,
} from '@/products/fleet/features/use-fleet';
import {
  useCreateFleetTelematicsConnection,
  useFleetTelematicsConnections,
  useFleetTelematicsIssues,
  useFleetTelematicsPositions,
  useImportFleetTelematicsCsv,
  useRotateFleetTelematicsToken,
} from '@/products/fleet/features/use-fleet-telematics';
import { fleetVehiclePath } from '@/products/fleet/fleet-paths';

const FleetPositionMap = lazy(() =>
  import('@/products/fleet/features/fleet-position-map').then((module) => ({
    default: module.FleetPositionMap,
  })),
);

const MAP_TILE_URL = import.meta.env.VITE_FLEET_MAP_TILE_URL?.trim() ?? '';

type MapRow = {
  vehicleId: string;
  licensePlate: string;
  holderName: string | null;
  placeLabel: string | null;
  observedAt: string | null;
  operationalStatus: FleetOperationalStatus;
  noSignal: boolean;
};

export function FleetBetriebKartePage() {
  usePageTitle('Betrieb — Karte');
  const me = useMe();
  const telematicsRead = hasPermission(me.data, PermissionCode.FLEET_TELEMATICS_READ);
  const telematicsManage = hasPermission(me.data, PermissionCode.FLEET_TELEMATICS_MANAGE);
  const manage = hasPermission(me.data, PermissionCode.FLEET_VEHICLES_MANAGE);
  const policy = useFleetPolicy();
  const summary = useFleetSummary(telematicsRead);
  const vehicles = useFleetVehicles(false);
  const positions = useFleetTelematicsPositions({
    enabled: telematicsRead && (policy.data?.telematicsEnabled ?? false),
    live: true,
  });
  const issues = useFleetTelematicsIssues(
    telematicsRead && (policy.data?.telematicsEnabled ?? false),
  );
  const connections = useFleetTelematicsConnections(telematicsManage);
  const createConnection = useCreateFleetTelematicsConnection();
  const rotateToken = useRotateFleetTelematicsToken();
  const importCsv = useImportFleetTelematicsCsv();
  const [connectionName, setConnectionName] = useState('');
  const [revealedToken, setRevealedToken] = useState<string | null>(null);

  const staleHours = policy.data?.telematicsStaleHours ?? 72;
  const telematicsOn = policy.data?.telematicsEnabled ?? false;

  const rows = useMemo(
    () =>
      buildMapRows({
        staleHours,
        positions: positions.data?.items ?? [],
        summaryToday: summary.data?.today ?? [],
        vehicles: manage && telematicsRead ? (vehicles.data?.items ?? []) : [],
        manage,
      }),
    [staleHours, positions.data, summary.data, vehicles.data, manage, telematicsRead],
  );

  const mapMarkers: FleetMapMarker[] = useMemo(
    () =>
      rows
        .filter(
          (row) =>
            row.observedAt != null &&
            positions.data?.items.some((p) => p.vehicleId === row.vehicleId),
        )
        .map((row) => {
          const pos = positions.data!.items.find((p) => p.vehicleId === row.vehicleId)!;
          return {
            vehicleId: row.vehicleId,
            licensePlate: row.licensePlate,
            latitude: pos.latitude,
            longitude: pos.longitude,
            placeLabel: pos.placeLabel,
          };
        }),
    [rows, positions.data],
  );

  async function onCreateConnection(event: FormEvent) {
    event.preventDefault();
    try {
      const result = await createConnection.mutateAsync({ name: connectionName.trim() });
      setRevealedToken(result.token);
      setConnectionName('');
      toast.success('Verbindung angelegt. Token jetzt kopieren — er wird nicht erneut angezeigt.');
    } catch {
      toast.error('Verbindung konnte nicht angelegt werden.');
    }
  }

  return (
    <div className="space-y-6">
      <PlatformPageHeader
        title="Karte"
        description="Letzte Standorte und Telematik-Signale der Fahrzeuge."
      />

      {!telematicsOn ? (
        <p className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-100">
          Telematik ist in der Richtlinie aus.
        </p>
      ) : null}

      {telematicsOn && telematicsRead ? (
        <>
          {MAP_TILE_URL ? (
            <Suspense
              fallback={<p className="text-sm text-muted-foreground">Karte wird geladen …</p>}
            >
              <FleetPositionMap markers={mapMarkers} tileUrl={MAP_TILE_URL} />
            </Suspense>
          ) : (
            <p className="text-sm text-muted-foreground">Kartenkacheln sind nicht konfiguriert.</p>
          )}

          <div className={`hidden md:block ${fleetTableFrameClassName}`}>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Kennzeichen</TableHead>
                  <TableHead>Fahrer</TableHead>
                  <TableHead>Ort</TableHead>
                  <TableHead>Zeitpunkt</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Signal</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.vehicleId}>
                    <TableCell>
                      <Link
                        className="font-medium hover:underline"
                        to={fleetVehiclePath(row.vehicleId)}
                      >
                        {row.licensePlate}
                      </Link>
                    </TableCell>
                    <TableCell>{row.holderName ?? '—'}</TableCell>
                    <TableCell>{row.placeLabel ?? '—'}</TableCell>
                    <TableCell>{formatDeDateTime(row.observedAt)}</TableCell>
                    <TableCell>
                      <FleetStatusBadge status={row.operationalStatus} />
                    </TableCell>
                    <TableCell>
                      {row.noSignal ? (
                        <FleetNoSignalBadge />
                      ) : (
                        <span className="text-sm">Aktuell</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <ul className={`divide-y md:hidden ${fleetTableFrameClassName}`}>
            {rows.map((row) => (
              <li key={row.vehicleId} className="space-y-2 p-3">
                <div className="flex items-start justify-between gap-2">
                  <Link
                    className="text-lg font-semibold text-primary hover:underline"
                    to={fleetVehiclePath(row.vehicleId)}
                  >
                    {row.licensePlate}
                  </Link>
                  {row.noSignal ? (
                    <FleetNoSignalBadge />
                  ) : (
                    <span className="text-sm text-muted-foreground">Aktuell</span>
                  )}
                </div>
                <p className="text-sm">{row.holderName ?? 'Kein Fahrer'}</p>
                <p className="text-sm text-muted-foreground">
                  {row.placeLabel ?? 'Kein Ort'} · {formatDeDateTime(row.observedAt)}
                </p>
                <FleetStatusBadge status={row.operationalStatus} />
              </li>
            ))}
          </ul>
        </>
      ) : null}

      {telematicsOn && telematicsManage ? (
        <Card>
          <CardHeader>
            <CardTitle>Telematik-Verbindung</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <form
              className="flex flex-col gap-3 sm:flex-row sm:items-end"
              onSubmit={(e) => void onCreateConnection(e)}
            >
              <div className="flex-1">
                <Label htmlFor="conn-name">Name der Verbindung</Label>
                <Input
                  id="conn-name"
                  className="mt-2"
                  value={connectionName}
                  onChange={(e) => setConnectionName(e.target.value)}
                  required
                />
              </div>
              <Button type="submit" disabled={createConnection.isPending}>
                Verbindung anlegen
              </Button>
            </form>
            {revealedToken ? (
              <div className="rounded-md border bg-muted/40 p-3 text-sm">
                <p className="font-medium">Token (einmalig sichtbar)</p>
                <p className="mt-2 font-mono text-xs break-all">{revealedToken}</p>
              </div>
            ) : null}
            <ul className="space-y-2 text-sm">
              {connections.data?.items.map((conn) => (
                <li
                  key={conn.id}
                  className="flex flex-wrap items-center justify-between gap-2 rounded border px-3 py-2"
                >
                  <span>{conn.name}</span>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={rotateToken.isPending}
                    onClick={() => {
                      void rotateToken.mutateAsync(conn.id).then((res) => {
                        setRevealedToken(res.token);
                        toast.success('Neues Token erzeugt — jetzt kopieren.');
                      });
                    }}
                  >
                    Token erneuern
                  </Button>
                </li>
              ))}
            </ul>
            <div>
              <Label htmlFor="telematics-csv">CSV-Import</Label>
              <Input
                id="telematics-csv"
                className="mt-2"
                type="file"
                accept=".csv,text/csv"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (!file) return;
                  void importCsv
                    .mutateAsync(file)
                    .then(() => toast.success('Import abgeschlossen.'));
                  event.target.value = '';
                }}
              />
            </div>
          </CardContent>
        </Card>
      ) : null}

      {telematicsOn && telematicsRead ? (
        <Card>
          <CardHeader>
            <CardTitle>Ausnahmen</CardTitle>
          </CardHeader>
          <CardContent>
            {issues.data?.items.length === 0 ? (
              <p className="text-sm text-muted-foreground">Keine offenen Telematik-Ausnahmen.</p>
            ) : (
              <ul className="space-y-2 text-sm">
                {issues.data?.items.map((issue) => (
                  <li key={issue.id} className="rounded border px-3 py-2">
                    <span className="font-medium">
                      {FLEET_TELEMATICS_ISSUE_LABELS[issue.reason] ?? issue.reason}
                    </span>
                    <span className="text-muted-foreground"> — {issue.externalRef}</span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}

function buildMapRows(input: {
  staleHours: number;
  positions: { vehicleId: string; placeLabel: string | null; observedAt: string }[];
  summaryToday: {
    vehicleId: string;
    licensePlate: string;
    holderName: string | null;
    operationalStatus: FleetOperationalStatus;
  }[];
  vehicles: {
    id: string;
    licensePlate: string;
    operationalStatus: FleetOperationalStatus;
    holderName: string | null;
  }[];
  manage: boolean;
}): MapRow[] {
  const staleMs = input.staleHours * 3_600_000;
  const positionByVehicle = new Map(input.positions.map((p) => [p.vehicleId, p]));
  const base = input.manage
    ? input.vehicles.map((v) => ({
        vehicleId: v.id,
        licensePlate: v.licensePlate,
        holderName: v.holderName,
        operationalStatus: v.operationalStatus,
      }))
    : input.summaryToday.map((row) => ({
        vehicleId: row.vehicleId,
        licensePlate: row.licensePlate,
        holderName: row.holderName,
        operationalStatus: row.operationalStatus,
      }));

  return base.map((row) => {
    const pos = positionByVehicle.get(row.vehicleId);
    const observedAt = pos?.observedAt ?? null;
    const noSignal = !observedAt || Date.now() - new Date(observedAt).getTime() > staleMs;
    return {
      ...row,
      placeLabel: pos?.placeLabel ?? null,
      observedAt,
      noSignal,
    };
  });
}
